import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import ProductService from "../../../../service/ProductService";
import { openConventionWindow } from "./PhysicalStoreConventionText";
import localeText from "../../../../locale";
import QrCodePayDialog from "../../components/QrCodePay/QrCodePayDialog";
import CreateTransactionDialog from "../../components/CreateTransactionDialog/CreateTransactionDialog";
import { Dialog } from "primereact/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { formatPrice } from "../../../../utilities/formatPrice";

const physicalStoreService = new PhysicalStoreService();
const productService = new ProductService();

const FieldRow = ({ label, children }) => (
  <div className="flex flex-column gap-1 mb-3">
    <label className="font-medium text-sm" style={{ color: "var(--theme-text-muted, #5c5c5c)" }}>
      {label}
    </label>
    {children}
  </div>
);

const ReadOnlyField = ({ value }) => (
  <div
    className="p-2 border-round"
    style={{
      background: "var(--surface-100, #f5f5f5)",
      border: "1px solid var(--surface-200, #e5e5e5)",
      color: "var(--theme-text, #171717)",
      minHeight: "2.25rem",
      fontSize: "0.95rem",
    }}
  >
    {value || "—"}
  </div>
);

const CONVENTION_ALREADY_SIGNED_CODE = 500260;

const extractCloudinaryId = (url) => url.split("/").pop().split(".")[0];

const PageHeader = () => (
  <div className="flex align-items-center gap-5 mb-4 mt-4">
    <div
      className="border-circle p-3"
      style={{ border: "1px solid var(--theme-border, #EBEBEB)", width: "54px", height: "54px" }}
    >
      <i className="pi pi-building text-xl" style={{ color: "var(--theme-text-muted, #5C5C5C)" }} />
    </div>
    <div>
      <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
        {localeText("physicalStoreConventionTitle")}
      </h2>
      <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
        {localeText("physicalStoreConventionSubtitle")}
      </p>
    </div>
  </div>
);

const PhysicalStorePage = ({ userInfo, currency }) => {
  const username = localStorage.getItem("__extraconomy_username");
  const { showSuccessToast, showErrorToast } = useToastContext();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [cameraPayVisible, setCameraPayVisible] = useState(false);
  const [newPaymentVisible, setNewPaymentVisible] = useState(false);
  const [loadingNewPayment, setLoadingNewPayment] = useState(false);
  const [qrTransaction, setQrTransaction] = useState(null);
  const [qrVisible, setQrVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 'convention' | 'pending' | 'approved'
  const [step, setStep] = useState("convention");
  const [conventionData, setConventionData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(null);

  const [commercialName, setCommercialName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [number, setNumber] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [province, setProvince] = useState("");
  const [phone, setPhone] = useState("");
  const [legalRepresentativeName, setLegalRepresentativeName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [merchantCategory, setMerchantCategory] = useState("");

  const [photoName, setPhotoName] = useState("");
  const [proofPhotoId, setProofPhotoId] = useState(null);
  const [proofPhotoUrl, setProofPhotoUrl] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const cancelledRef = useRef(false);

  const applyVerificationStatus = (statusRes) => {
    if (statusRes?.messageCode === 200001) {
      const status = statusRes.result?.verificationStatus;
      if (status === "APPROVED") {
        setStep("approved");
      } else if (status === "PENDING") {
        setStep("pending");
      } else if (status === "REJECTED") {
        setRejectionReason(statusRes.result?.rejectionReason || null);
        setStep("convention");
      } else {
        setStep("convention");
      }
    } else {
      setStep("convention");
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);

    Promise.all([
      physicalStoreService.getConventionPreview(username),
      physicalStoreService.verificationStatus(username).catch(() => null),
    ])
      .then(([previewRes, statusRes]) => {
        if (cancelledRef.current) return;
        const data = previewRes?.result ?? previewRes;
        const proof = statusRes?.result;

        setConventionData(data);
        setCommercialName(data?.commercialName || data?.storeName || "");
        setCompanyName(data?.companyName || proof?.physicalStoreName || "");
        setAddress(data?.address || proof?.physicalAddress || "");
        setNumber(data?.number || proof?.physicalNumber || "");
        setCity(data?.city || proof?.physicalCity || "");
        setPostalCode(data?.postalCode || proof?.physicalPostalCode || "");
        setProvince(
          data?.province || data?.state || proof?.physicalState || proof?.physicalDistrict || "",
        );
        setPhone(data?.phone || "");
        setLegalRepresentativeName(data?.legalRepresentativeName || "");
        setTaxId(data?.taxId || proof?.store?.documentNumber || "");
        setMerchantCategory(data?.merchantCategory || "");

        if (!data?.alreadySigned) {
          setStep("convention");
        } else {
          applyVerificationStatus(statusRes);
          if (statusRes?.result?.verificationStatus === "REJECTED" && proof?.proofPhotoUrl) {
            setProofPhotoUrl(proof.proofPhotoUrl);
            setProofPhotoId(proof.proofPhotoId || null);
            setPhotoName(proof.proofPhotoUrl.split("/").pop());
          }
        }
      })
      .catch(() => {
        if (!cancelledRef.current) {
          showErrorToast({ detail: localeText("physicalStoreConventionSignedError") });
        }
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoName(file.name);
    setProofPhotoId(null);
    setIsUploadingPhoto(true);

    try {
      const res = await productService.postUploadFile(null, file);

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        setPhotoName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setProofPhotoUrl(res.result);
      setProofPhotoId(extractCloudinaryId(res.result));
    } catch {
      showErrorToast({ code: 500000 });
      setPhotoName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleClearPhoto = (e) => {
    e.stopPropagation();
    setPhotoName("");
    setProofPhotoId(null);
    setProofPhotoUrl(null);
    setIsUploadingPhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSign = async () => {
    setSubmitting(true);
    try {
      const conventionRes = await physicalStoreService.signConvention({
        legalRepresentativeName,
        taxId,
        merchantCategory,
        commercialName,
        companyName,
        address,
        number,
        city,
        postalCode,
        province,
        phone,
        username,
        accepted: true,
      });

      const conventionOk = [200000, 200001, CONVENTION_ALREADY_SIGNED_CODE].includes(
        conventionRes?.messageCode,
      );

      if (!conventionOk) {
        showErrorToast({ code: conventionRes?.messageCode });
        return;
      }

      const proofRes = await physicalStoreService.submitProof({
        physicalStoreName: companyName,
        physicalAddress: address,
        physicalNumber: number,
        physicalDistrict: province,
        physicalCity: city,
        physicalState: province,
        physicalPostalCode: postalCode,
        physicalCountry: conventionData?.country || "",
        proofPhotoId,
        proofPhotoUrl,
        username,
      });

      const proofOk = [200000, 200001].includes(proofRes?.messageCode);

      if (!proofOk) {
        showErrorToast({ code: proofRes?.messageCode });
        return;
      }

      if (conventionRes.messageCode !== CONVENTION_ALREADY_SIGNED_CODE) {
        showSuccessToast({ detail: localeText("physicalStoreConventionSignedSuccess") });
      }

      setStep("pending");
    } catch {
      showErrorToast({ detail: localeText("physicalStoreConventionSignedError") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTransaction = async ({ amount, description }) => {
    setLoadingNewPayment(true);
    try {
      const res = await physicalStoreService.createTransaction({
        amount,
        description,
        user: userInfo?.username || username,
      });
      if (res.messageCode === 200000) {
        setNewPaymentVisible(false);
        setQrTransaction(res.result);
        setQrVisible(true);
        return;
      }
      showErrorToast({ code: res.messageCode });
    } catch {
      showErrorToast({ code: 500000 });
    } finally {
      setLoadingNewPayment(false);
    }
  };

  const canSubmit =
    accepted &&
    commercialName.trim() &&
    companyName.trim() &&
    address.trim() &&
    number.trim() &&
    city.trim() &&
    postalCode.trim() &&
    province.trim() &&
    phone.trim() &&
    legalRepresentativeName.trim() &&
    taxId.trim() &&
    merchantCategory.trim() &&
    proofPhotoId &&
    !isUploadingPhoto;

  if (loading) {
    return (
      <DashboardLayout>
        <StoreBreadcrumbs />
        <PageHeader />
        <div className="flex flex-column gap-3" style={{ maxWidth: 640 }}>
          <Skeleton height="2rem" />
          <Skeleton height="2rem" />
          <Skeleton height="2rem" />
          <Skeleton height="8rem" />
          <Skeleton height="2rem" width="50%" />
        </div>
      </DashboardLayout>
    );
  }

  if (step === "pending") {
    return (
      <DashboardLayout>
        <StoreBreadcrumbs />
        <PageHeader />
        <div
          className="flex flex-column align-items-center gap-3 border-round-lg p-5"
          style={{
            width: "100%",
            background: "var(--surface-50, #fafafa)",
            border: "1px solid var(--surface-200, #e5e5e5)",
          }}
        >
          <div
            className="border-circle flex align-items-center justify-content-center"
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
            }}
          >
            <i className="pi pi-clock text-2xl" style={{ color: "var(--theme-text-muted)" }} />
          </div>
          <p className="m-0 font-semibold text-base" style={{ color: "var(--theme-text)" }}>
            {localeText("physicalStoreConventionPendingTitle")}
          </p>
          <p className="m-0 text-sm text-center" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("physicalStoreConventionPendingMessage")}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (step === "approved") {
    const actionCard = (icon, title, desc, onClick) => (
      <div
        className="flex align-items-center gap-3 border-round-lg p-4 cursor-pointer"
        style={{
          background: "var(--surface-50, #fafafa)",
          border: "1px solid var(--surface-200, #e5e5e5)",
          transition: "border-color 0.15s",
        }}
        onClick={onClick}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--theme-primary, #1DAF61)")}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--surface-200, #e5e5e5)")}
      >
        <div
          className="border-circle flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "color-mix(in srgb, var(--theme-primary, #1DAF61) 12%, transparent)",
          }}
        >
          <i className={`${icon} text-xl`} style={{ color: "var(--theme-primary, #1DAF61)" }} />
        </div>
        <div className="flex flex-column gap-1">
          <span className="font-semibold text-sm" style={{ color: "var(--theme-text)" }}>
            {title}
          </span>
          <span className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
            {desc}
          </span>
        </div>
        <i className="pi pi-chevron-right ml-auto" style={{ color: "var(--theme-text-muted)" }} />
      </div>
    );

    return (
      <DashboardLayout>
        <StoreBreadcrumbs />
        <PageHeader />
        <div style={{ maxWidth: 560 }}>
          <div
            className="flex align-items-center gap-3 border-round-lg p-4 mb-4"
            style={{
              background: "color-mix(in srgb, var(--theme-primary, #1DAF61) 8%, transparent)",
              border: "1px solid color-mix(in srgb, var(--theme-primary, #1DAF61) 25%, transparent)",
            }}
          >
            <div
              className="border-circle flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: "color-mix(in srgb, var(--theme-primary, #1DAF61) 15%, transparent)",
              }}
            >
              <i
                className="pi pi-check-circle text-xl"
                style={{ color: "var(--theme-primary, #1DAF61)" }}
              />
            </div>
            <div className="flex flex-column gap-1">
              <span className="font-semibold text-sm" style={{ color: "var(--theme-text)" }}>
                {localeText("physicalStoreConventionApprovedTitle")}
              </span>
              <span className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
                {localeText("physicalStoreConventionApprovedMessage")}
              </span>
            </div>
          </div>

          <div className="flex flex-column gap-2">
            {actionCard(
              "pi pi-qrcode",
              localeText("physicalStoreNewPayment"),
              localeText("physicalStoreNewPaymentDesc"),
              () => setNewPaymentVisible(true),
            )}
            {actionCard(
              "pi pi-list",
              localeText("physicalStorePaymentsList"),
              localeText("physicalStorePaymentsListDesc"),
              () => history.push("/dashboard-store-transactions"),
            )}
            {actionCard(
              "pi pi-camera",
              localeText("physicalStoreCameraPayment"),
              localeText("physicalStoreCameraPaymentDesc"),
              () => setCameraPayVisible(true),
            )}
          </div>
        </div>
        <CreateTransactionDialog
          visible={newPaymentVisible}
          onHide={() => setNewPaymentVisible(false)}
          onSubmit={handleCreateTransaction}
          loading={loadingNewPayment}
          currency={currency}
          title={localeText("createChargeWithQrCode")}
          subtitle={localeText("createChargeWithQrCodeDescription")}
        />

        <Dialog
          header={localeText("qrCodeOfTransaction")}
          visible={qrVisible}
          className="w-30rem m-2"
          onHide={() => { setQrVisible(false); setQrTransaction(null); }}
          draggable={false}
        >
          {qrTransaction?.id && (
            <div className="flex flex-column justify-content-center align-items-center gap-3 py-2">
              <div
                className="border-round p-3"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <QRCodeCanvas value={`${qrTransaction.id}`} size={200} />
              </div>
              <p className="m-0 text-sm" style={{ color: "var(--theme-text-muted)" }}>
                {localeText("awaitingPayment")}...
              </p>
              <p className="m-0 font-semibold" style={{ color: "var(--theme-text)" }}>
                {currency} {formatPrice(qrTransaction.amountPaid)}
              </p>
            </div>
          )}
        </Dialog>

        <QrCodePayDialog
          visible={cameraPayVisible}
          onHide={() => setCameraPayVisible(false)}
          user={userInfo}
          currency={currency}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      <PageHeader />

      <div style={{ maxWidth: 640 }}>
        {rejectionReason && (
          <div
            className="flex align-items-start gap-2 border-round-lg p-3 mb-4"
            style={{
              backgroundColor: "color-mix(in srgb, #eb4034 12%, transparent)",
              border: "1px solid color-mix(in srgb, #eb4034 30%, transparent)",
            }}
          >
            <i
              className="pi pi-times-circle text-sm mt-1"
              style={{ color: "#eb4034", flexShrink: 0 }}
            />
            <div className="flex flex-column gap-1">
              <span className="font-semibold text-sm" style={{ color: "#eb4034" }}>
                {localeText("physicalStoreConventionRejectedTitle")}
              </span>
              <span className="text-sm" style={{ color: "var(--theme-text)" }}>
                {rejectionReason}
              </span>
            </div>
          </div>
        )}

        <div
          className="border-round-lg p-4 mb-4"
          style={{
            background: "var(--surface-50, #fafafa)",
            border: "1px solid var(--surface-200, #e5e5e5)",
          }}
        >
          {conventionData?.storeName && (
            <FieldRow label={localeText("physicalStore")}>
              <ReadOnlyField value={conventionData.storeName} />
            </FieldRow>
          )}

          {conventionData?.ownerName && (
            <FieldRow label={localeText("name") || "Nome"}>
              <ReadOnlyField value={conventionData.ownerName} />
            </FieldRow>
          )}

          {conventionData?.email && (
            <FieldRow label="E-mail">
              <ReadOnlyField value={conventionData.email} />
            </FieldRow>
          )}

          <FieldRow label={localeText("physicalStoreConventionCommercialName")}>
            <InputText
              value={commercialName}
              onChange={(e) => setCommercialName(e.target.value)}
              disabled={!!conventionData?.commercialName && !rejectionReason}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionCompanyName")}>
            <InputText
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={!!conventionData?.companyName}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionAddress")}>
            <InputText
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={!!conventionData?.address}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionNumber")}>
            <InputText
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={!!conventionData?.number}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionCity")}>
            <InputText
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!!conventionData?.city}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionPostalCode")}>
            <InputText
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              disabled={!!conventionData?.postalCode}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionProvince")}>
            <InputText
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              disabled={!!conventionData?.province}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionPhone")}>
            <InputText
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!!conventionData?.phone}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionLegalRepresentative")}>
            <InputText
              value={legalRepresentativeName}
              onChange={(e) => setLegalRepresentativeName(e.target.value)}
              disabled={!!conventionData?.legalRepresentativeName && !rejectionReason}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionTaxId")}>
            <InputText
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              disabled={!!conventionData?.taxId}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionMerchantCategory")}>
            <InputText
              value={merchantCategory}
              onChange={(e) => setMerchantCategory(e.target.value)}
              disabled={!!conventionData?.merchantCategory && !rejectionReason}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={localeText("physicalStoreConventionProofPhoto")}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div
              className="flex align-items-center gap-2 border-round p-2 cursor-pointer"
              style={{ border: "1px solid var(--theme-border, #EBEBEB)", minHeight: "42px" }}
              onClick={() => !isUploadingPhoto && fileInputRef.current?.click()}
            >
              {isUploadingPhoto ? (
                <i
                  className="pi pi-spin pi-spinner text-sm"
                  style={{ color: "var(--theme-text-muted)" }}
                />
              ) : (
                <i
                  className={`pi ${proofPhotoId ? "pi-check-circle" : "pi-image"} text-sm`}
                  style={{
                    color: proofPhotoId
                      ? "var(--theme-primary, #1DAF61)"
                      : "var(--theme-text-muted)",
                  }}
                />
              )}
              <span
                className="text-sm flex-1"
                style={{
                  color: photoName ? "var(--theme-text)" : "var(--theme-text-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isUploadingPhoto
                  ? localeText("physicalStoreConventionUploading") || "Enviando..."
                  : photoName ||
                    localeText("physicalStoreConventionProofPhotoPlaceholder") ||
                    "Clique para selecionar uma imagem"}
              </span>
              {photoName && !isUploadingPhoto && (
                <i
                  className="pi pi-times text-xs cursor-pointer"
                  style={{ color: "var(--theme-text-muted)" }}
                  onClick={handleClearPhoto}
                />
              )}
            </div>
            {proofPhotoUrl && !isUploadingPhoto && (
              <div className="flex align-items-center gap-2 mt-2">
                <img
                  src={proofPhotoUrl}
                  alt="proof"
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: "1px solid var(--surface-200, #e5e5e5)",
                    flexShrink: 0,
                  }}
                />
                <a
                  href={proofPhotoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm"
                  style={{ color: "var(--theme-primary, #1DAF61)" }}
                >
                  {localeText("openPhoto")}
                </a>
              </div>
            )}
          </FieldRow>

          <div className="mt-2">
            <button
              type="button"
              className="p-link text-sm"
              style={{
                color: "var(--theme-primary, #1DAF61)",
                textDecoration: "underline",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
              onClick={() =>
                openConventionWindow({
                  legalRepresentativeName,
                  taxId,
                  merchantCategory,
                  company: conventionData?.company,
                  storeName: conventionData?.storeName,
                  address,
                  number,
                  postalCode,
                  city,
                  state: province,
                  phone,
                  email: conventionData?.email,
                  date: new Date().toLocaleDateString(),
                })
              }
            >
              <i className="pi pi-file-pdf mr-1" />
              {localeText("physicalStoreConventionViewDoc") || "Ver termos da convenção"}
            </button>
          </div>
        </div>

        <div className="flex align-items-start gap-2 mb-4">
          <Checkbox
            inputId="accept-convention"
            checked={accepted}
            onChange={(e) => setAccepted(e.checked)}
          />
          <label
            htmlFor="accept-convention"
            className="text-sm cursor-pointer"
            style={{ color: "var(--theme-text, #171717)" }}
          >
            {localeText("physicalStoreConventionAccept")}
          </label>
        </div>

        <Button
          label={localeText("physicalStoreConventionConfirm")}
          icon="pi pi-check"
          disabled={!canSubmit}
          loading={submitting}
          onClick={handleSign}
        />
      </div>
    </DashboardLayout>
  );
};

export default PhysicalStorePage;
